// BuildingWalls_Pure.ts — Buildings Set2 F2: the floor-level wall MERGE engine (§3).
//
// F1 translated the outline to the CENTERLINE convention and kept walls per-room. F2 introduces
// the first-class floor-level wall: `MergeFloorWalls_Pure` takes each room's CENTERLINE ring +
// resolved `wallThickness` and produces a `FloorWallPlan` whose segments are the MERGED walls of
// the floor — shared walls (two adjacent rooms' coincident anti-parallel edges) collapse into ONE
// segment with max-wins thickness, instead of the Set1 double-wall. Openings, mitering, and the
// wall SOLID come later (F3, §6.1); this file only produces the wall *plan* the F3 builder feeds on.
//
// Deliberately pure (no three / mobx-graphlink / DOM / MapObject), so the Node vitest lane imports
// it directly — the same convention as `Offset_Pure.ts` / `Roof_Pure.ts` and the §3.5 test suite.
//
// Exact geometry (centerline convention, Set2 §2/§3):
//   - a room's CENTERLINE ring is CCW + building-local; its exterior face is `+t/2` along the
//     outward normal, its interior face `−t/2`.
//   - shared walls span `±thickness/2` about the centerline with the resolved (max-wins) thickness;
//     interior-face is relative to the first claimant.
//   - The unions (`centerlineUnion`, `innerFaceUnion`) are built by a localized cell-cover+coincident
//     cancellation pass, which for axis-aligned rectangles is byte-exact against the Roof_Pure
//     coordinate-lattice result (the §3.5 oracle) and for well-formed general plans handles shared
//     edges + covered (nested/overlapping) edges alike.
import type {Vec2Simple} from "../../Utils/ThreeJS/VectorStructs.js";
import {NormalizeFootprint} from "../../Server/DB/mapObjects/Building/BuildingFootprint_Pure.js";
import {CollapseSelfIntersections} from "./Offset_Pure.js";
import {TraceEdgesToPolygon, OptimizePolygon, IsPointInPolygon} from "../../Utils/ThreeJS/PolygonUtils.js";

/** Default coincidence tolerance (metres) for "same edge" snapping (§3.3.2). */
const DEFAULT_COINCIDENCE_TOL = 1e-3;
/** Below this perpendicular gap a near-coincident non-merged pair is almost certainly an authoring
 *  slip (a "thin cavity"), so we warn (§3.3.3). */
const THIN_CAVITY_TOL = 0.05;
/** A perimeter-midpoint vs another room's boundary within this distance is treated as ON the shared
 *  boundary (kept) rather than covered — guards the ambiguous ray-cast for boundary-adjacent points
 *  without dropping genuinely-covered spans. */
const BOUNDARY_GUARD_EPS = 1e-6;

export type RoomWallInput = {
	roomObjID: string;
	/** Centerline ring, CCW, closed (trailing dup optional), building-local. */
	centerline: Vec2Simple[];
	/** Resolved per-room wall thickness (fallback chain already applied). */
	wallThickness: number;
	/** Index into the CALLER's source ring, aligned 1:1 with `centerline` (i.e.
	 *  `sourceRing[i]` is the centerline vertex `centerline[i]` was derived from). Set by the
	 *  accessor layer so later phases can map a resolved output ring (e.g. `innerFacePerRoom`) back
	 *  to the AUTHORED points for the 2D overlay. Purely informational — the merge math ignores it.
	 *  Omitted (undefined) by pure-layer callers/tests that have no separate source ring. */
	sourceRing?: Vec2Simple[];
};

export type FloorWallMergeInput = {
	rooms: RoomWallInput[];
	/** Snap tolerance for "coincident" endpoints/edges in metres. Default 1e-3. */
	coincidenceTolerance?: number;
};

/** One merged wall segment of a floor (the FIRST-CLASS floor wall, Set2 §3.2). `start`/`end`
 *  are the segment's CENTERLINE endpoint(s); the wall spans `∓wallThickness/2` either side. When
 *  `claimantRoomIDs.length >= 2` this is a SHARED (interior) wall — two rooms' coincident edges
 *  collapsed to a single solid. */
export type FloorWallSegment = {
	/** Centerline endpoints (building-local). */
	start: Vec2Simple;
	end: Vec2Simple;
	length: number;
	/** Unit outward normal (CCW right-normal), pointing away from the segment's first claimant. */
	normal: Vec2Simple;
	/** Resolved wall thickness (max-wins over all claimants). */
	wallThickness: number;
	/** Interior-face endpoints — the segment's centerline endpoints offset by `−wallThickness/2`·normal,
	 *  with the segment's ENDS MITERED against the neighbouring centerline edges of the claimant room
	 *  (§3.4), so the emitted band is watertight at every corner and byte-identical to
	 *  `Offset_Pure.CenterlineToWallRings` for an all-exterior floor. Pre-offset: the F3 wall builder
	 *  consumes these directly and must NOT re-offset. */
	innerStart: Vec2Simple;
	innerEnd: Vec2Simple;
	/** Exterior-face endpoints (centerline plus `wallThickness/2`·normal, mitered). */
	outerStart: Vec2Simple;
	outerEnd: Vec2Simple;
	/** The along-the-wall length of the INTERIOR face (`|innerEnd − innerStart|`). This is the arc
	 *  length `alongCenter` is measured against (`ResolveOpeningsForFloor`) and that the wall builder
	 *  punches with, so the hole and the opening model agree (the F1 matched-pair invariant).
	 *  It is NOT `length` (the centerline length): the miter at a corner lengthens each face by its
	 *  own amount. */
	innerLength: number;
	/** Rooms that border this segment (≥1; ≥2 means it's a shared/interior wall). */
	claimantRoomIDs: string[];
	/** True when `>=2` rooms share this wall. */
	shared: boolean;
	/** True when one of this segment's ends butts into the INTERIOR of another segment (a T-join).
	 *  Equivalent to `tJunctionStarts || tJunctionEnds` (kept as the one-flag summary the
	 *  diagnostics/UI read). */
	tJunction: boolean;
	/** Per-END T-join flags: this segment's `start` (resp. `end`) lands on the INTERIOR of another segment, so its band LACKS a neighbouring segment to close its cross-section. Set2 §3.4 / F4: `WallSolidBuilder_FloorSegments` emits a BUTT CAP at each flagged end — without it the end is open, a visible hole wherever the ending band sticks out past the through wall (a thick interior wall meeting a thin one). Per end because a wall can be a T at BOTH ends. */
	tJunctionStarts: boolean;
	tJunctionEnds: boolean;
};

export type FloorWallPlan = {
	/** The merged (first-class) wall segments of the floor. */
	segments: FloorWallSegment[];
	/** The centerline footprint of the WHOLE floor (union of room centerlines), CCW + closed. */
	centerlineUnion: Vec2Simple[];
	/** The inner-face footprint of the whole floor (centerline inset by t/2 where the edge is
	 *  exterior; flush where shared), CCW + closed. */
	innerFaceUnion: Vec2Simple[];
	/** Per-room inner face (the finish-plane outline, `floorTop` + ceiling plane), CCW + closed. */
	innerFacePerRoom: Array<{roomObjID: string, ring: Vec2Simple[]}>;
	/** Per-room SLAB outline (§6.2): flush with the centerline on shared edges, expanded to the
	 *  OUTER face on exterior ones — so a room's slab supports its full exterior wall while
	 *  adjacent rooms' slabs meet (not overlap) at shared centerlines. */
	slabRingPerRoom: Array<{roomObjID: string, ring: Vec2Simple[]}>;
	/** Per-room wall BAND as index-aligned face polylines (Set2 §9) — the 2D overlay draws these
	 *  instead of re-deriving the faces with `OffsetOutward`. Same rooms/order as
	 *  `innerFacePerRoom`; each entry's `inner` is the ring `innerFacePerRoom` closes. */
	wallsPerRoom: RoomFaceLines[];
	/** Thickness conflicts, thin cavities, T-junctions, degenerate offsets, non-mergeable
	 *  near-coincident edges, degenerate/zero-area rooms. */
	warnings: string[];
};

// ---------------------------------------------------------------------------
// Small pure geometry helpers
// ---------------------------------------------------------------------------
function signedArea(pts: Vec2Simple[]): number {
	let area = 0;
	for (let i = 0; i < pts.length; i++) {
		const a = pts[i], b = pts[(i + 1) % pts.length];
		area += a[0] * b[1] - b[0] * a[1];
	}
	return area / 2;
}

function closeRing(pts: Vec2Simple[]): Vec2Simple[] {
	if (pts.length == 0) return [];
	const a = pts[0], b = pts[pts.length - 1];
	if (Math.abs(a[0] - b[0]) < 1e-12 && Math.abs(a[1] - b[1]) < 1e-12) return pts.slice();
	return [...pts, [a[0], a[1]] as Vec2Simple];
}

function gcd(a: number, b: number): number {
	a = Math.abs(a); b = Math.abs(b);
	while (b) { const t = a % b; a = b; b = t; }
	return a || 1;
}

function snapCoord(v: number, tol: number): number {
	const s = Math.round(v / tol) * tol;
	return Math.abs(s) < tol * 1e-6 ? 0 : s;
}

function snapPoint(p: Vec2Simple, tol: number): Vec2Simple {
	return [snapCoord(p[0], tol), snapCoord(p[1], tol)] as Vec2Simple;
}

function pointKey(p: Vec2Simple): string {
	return `${p[0]},${p[1]}`;
}

/** Canonical ordered key for an UNORDERED endpoint pair (so room A's P→Q and room B's Q→P match). */
function pairKey(a: Vec2Simple, b: Vec2Simple): string {
	const ka = pointKey(a), kb = pointKey(b);
	return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}

function distPointSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
	const dx = bx - ax, dy = by - ay;
	const l2 = dx * dx + dy * dy;
	let t = l2 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
	t = Math.max(0, Math.min(1, t));
	return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

function distToRingPoint(x: number, y: number, ring: Vec2Simple[]): number {
	if (ring.length == 0) return Infinity;
	let dmin = Infinity;
	for (let i = 0; i < ring.length; i++) {
		const a = ring[i], b = ring[(i + 1) % ring.length];
		dmin = Math.min(dmin, distPointSegment(x, y, a[0], a[1], b[0], b[1]));
	}
	return dmin;
}

/** Intersect two infinite lines P1 + t·u1 and P2 + s·u2. Null when parallel. */
function lineIntersect(p1: Vec2Simple, u1: Vec2Simple, p2: Vec2Simple, u2: Vec2Simple): Vec2Simple|null {
	const denom = u1[0] * u2[1] - u1[1] * u2[0];
	if (Math.abs(denom) < 1e-12) return null;
	const rx = p2[0] - p1[0], ry = p2[1] - p1[1];
	const t = (rx * u2[1] - ry * u2[0]) / denom;
	return [p1[0] + t * u1[0], p1[1] + t * u1[1]] as Vec2Simple;
}

/** Raw directed edge of a room's ring (open CCW). `edgeIndex` is the ring-vertex index `i` the
 *  edge starts at (edge i spans `ring[i] → ring[(i+1) % n]`), so a resolved face ring can be
 *  indexed by the same `i`. */
type RawEdge = {
	start: Vec2Simple;
	end: Vec2Simple;
	length: number;
	normal: Vec2Simple;
	roomObjID: string;
	wallThickness: number;
	edgeIndex: number;
};

function edgesOfRoom(ring: Vec2Simple[], roomObjID: string, wallThickness: number): RawEdge[] {
	const edges: RawEdge[] = [];
	const n = ring.length;
	for (let i = 0; i < n; i++) {
		const start = ring[i];
		const end = ring[(i + 1) % n];
		const dx = end[0] - start[0], dy = end[1] - start[1];
		const length = Math.hypot(dx, dy);
		if (length < 1e-12) continue;
		const normal = [dy / length, -dx / length] as Vec2Simple; // CCW right-normal (outward)
		edges.push({start, end, length, normal, roomObjID, wallThickness, edgeIndex: i});
	}
	return edges;
}

/** The room's MITERED inner/exterior face rings, indexed by ring vertex (`face[i]` is the face
 *  vertex at centerline vertex `i`). Set2 §3.4: each corner is the intersection of its two incident
 *  edges' offset lines — the same `1/cos(θ/2)` miter `OffsetOutward` produces for a whole ring —
 *  but with each edge offset by ITS OWN resolved (max-wins merged) `thickness/2`, because a shared
 *  wall's thickness is the max over its claimants and can differ from the room's other walls.
 *  Faces are NOT closed (n vertices), matching the ring they index. */
function miteredFaceRings(ring: Vec2Simple[], thicknessByPair: Map<string, number>, defaultThickness: number): {inner: Vec2Simple[], outer: Vec2Simple[]} {
	const n = ring.length;
	const dirs: Vec2Simple[] = [];
	const normals: Vec2Simple[] = [];
	const halfT: number[] = [];
	for (let i = 0; i < n; i++) {
		const a = ring[i], b = ring[(i + 1) % n];
		const dx = b[0] - a[0], dy = b[1] - a[1];
		const len = Math.hypot(dx, dy) || 1e-9;
		dirs.push([dx / len, dy / len] as Vec2Simple);
		normals.push([dy / len, -dx / len] as Vec2Simple);
		halfT.push((thicknessByPair.get(pairKey(a, b)) ?? defaultThickness) / 2);
	}
	const inner: Vec2Simple[] = [];
	const outer: Vec2Simple[] = [];
	for (let k = 0; k < n; k++) {
		const ePrev = (k - 1 + n) % n, ek = k;
		// Anchor a point on each incident edge's offset line, then intersect the two lines.
		const innerAnchorPrev: Vec2Simple = [ring[k][0] - normals[ePrev][0] * halfT[ePrev], ring[k][1] - normals[ePrev][1] * halfT[ePrev]];
		const innerAnchorK: Vec2Simple = [ring[k][0] - normals[ek][0] * halfT[ek], ring[k][1] - normals[ek][1] * halfT[ek]];
		const outerAnchorPrev: Vec2Simple = [ring[k][0] + normals[ePrev][0] * halfT[ePrev], ring[k][1] + normals[ePrev][1] * halfT[ePrev]];
		const outerAnchorK: Vec2Simple = [ring[k][0] + normals[ek][0] * halfT[ek], ring[k][1] + normals[ek][1] * halfT[ek]];
		inner.push(lineIntersect(innerAnchorPrev, dirs[ePrev], innerAnchorK, dirs[ek]) ?? innerAnchorK);
		outer.push(lineIntersect(outerAnchorPrev, dirs[ePrev], outerAnchorK, dirs[ek]) ?? outerAnchorK);
	}
	return {inner, outer};
}

// ---------------------------------------------------------------------------
// Shared-edge detection (from centerline rings)
// ---------------------------------------------------------------------------
/** The set of unordered endpoint-pair keys claimed by >=2 distinct rooms — i.e. SHARED (coincident
 *  anti-parallel) edges that should collapse to a single wall, and which are interior (not part of
 *  the union boundary). */
/** §11 collinear sub-span fix. When two rooms' centerline edges are collinear and PARTIALLY
 *  overlap (one edge's span strictly inside the other's, e.g. a 4m room edge along the middle
 *  of an 11m wall), the exact-key grouping cannot merge them — the result is two stacked bands
 *  and an Opening punching only one of them. This pass cuts BOTH edges at every interior
 *  endpoint of the other's span, so the mutually-overlapping sub-spans share exact endpoints
 *  and merge downstream. Returns the rooms with their centerline rings replaced by the split
 *  rings (same winding, same order otherwise).
 *
 *  Only pure collinear overlaps are split (cross product ~ 0); transversal T-joins are left to
 *  the existing butt-cap logic, and edges merely TOUCHING end-to-end are left alone. */
function splitCollinearOverlaps(
	rooms: Array<{roomObjID: string, centerline: Vec2Simple[], wallThickness: number}>,
	tol: number,
	warnings: string[],
): Array<{roomObjID: string, centerline: Vec2Simple[], wallThickness: number}> {
	type Edge = {roomIdx: number, edgeIdx: number, a: Vec2Simple, b: Vec2Simple, nx: number, ny: number, len: number};
	const edges: Edge[] = [];
	for (const [roomIdx, r] of rooms.entries()) {
		const ring = r.centerline;
		const n = ring.length;
		for (let i = 0; i < n; i++) {
			const a = ring[i], b = ring[(i + 1) % n];
			const dx = b[0] - a[0], dy = b[1] - a[1];
			const len = Math.hypot(dx, dy);
			if (len < 1e-9) continue;
			edges.push({roomIdx, edgeIdx: i, a, b, nx: dx / len, ny: dy / len, len});
		}
	}

	// Collect split parameters per (roomIdx, edgeIdx): positions along the edge where another
	// collinear edge's endpoint lands strictly inside it.
	const cuts = new Map<string, number[]>(); // key roomIdx:edgeIdx → [t...]
	const addCut = (e: Edge, t: number)=>{
		if (t <= 1e-6 || t >= 1 - 1e-6) return; // touching end-to-end is not a split
		const k = `${e.roomIdx}:${e.edgeIdx}`;
		let arr = cuts.get(k);
		if (!arr) { arr = []; cuts.set(k, arr); }
		arr.push(t);
	};

	for (let i = 0; i < edges.length; i++) {
		for (let j = 0; j < edges.length; j++) {
			if (i == j) continue;
			const ei = edges[i], ej = edges[j];
			if (ei.roomIdx == ej.roomIdx) continue; // a room's own edges never overlap each other
			// Collinear? (parallel unit dirs, and ej.a lies on ei's line)
			const cross = ei.nx * ej.ny - ei.ny * ej.nx;
			if (Math.abs(cross) > 1e-6) continue;
			const relX = ej.a[0] - ei.a[0], relY = ej.a[1] - ei.a[1];
			const perp = relX * (-ei.ny) + relY * ei.nx; // distance of ej.a from ei's line
			if (Math.abs(perp) > 1e-4) continue;
			// Project both of ej's endpoints onto ei's axis; cut ei where an endpoint lands inside.
			for (const p of [ej.a, ej.b]) {
				const t = ((p[0] - ei.a[0]) * ei.nx + (p[1] - ei.a[1]) * ei.ny) / ei.len;
				// Only cut if the point is strictly inside ei AND ei's span actually overlaps ej's
				// (the endpoint between ej.a/ej.b along ej's axis) — a T-end touching the LINE but
				// beyond ej's span is just a passing wall, not an overlap.
				const tj = ((p[0] - ej.a[0]) * ej.nx + (p[1] - ej.a[1]) * ej.ny) / ej.len;
				if (tj < -1e-6 || tj > 1 + 1e-6) continue;
				addCut(ei, t);
				// Reciprocal: ei's endpoints cut ej too.
			}
			for (const p of [ei.a, ei.b]) {
				const t = ((p[0] - ej.a[0]) * ej.nx + (p[1] - ej.a[1]) * ej.ny) / ej.len;
				const ti = ((p[0] - ei.a[0]) * ei.nx + (p[1] - ei.a[1]) * ei.ny) / ei.len;
				if (ti < -1e-6 || ti > 1 + 1e-6) continue;
				addCut(ej, t);
			}
		}
	}

	if (cuts.size == 0) return rooms;

	// Rebuild each affected room's ring with its cut edges replaced by their sub-spans.
	const out: Array<{roomObjID: string, centerline: Vec2Simple[], wallThickness: number}> = [];
	for (const [roomIdx, r] of rooms.entries()) {
		const ring = r.centerline;
		const n = ring.length;
		const newRing: Vec2Simple[] = [];
		for (let i = 0; i < n; i++) {
			const a = ring[i], b = ring[(i + 1) % n];
			newRing.push(a);
			const ts = (cuts.get(`${roomIdx}:${i}`) ?? []).slice().sort((x, y)=>x - y);
			if (ts.length == 0) continue;
			for (const t of ts) {
				newRing.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
			}
		}
		const cleaned = NormalizeFootprint(newRing.map(p=>snapPoint(p, tol)), false);
		if (cleaned.length < 3 || Math.abs(signedArea(cleaned)) < 1e-12) {
			warnings.push(`Room ${r.roomObjID}: ring degenerated after collinear split; using the unsplit ring.`);
			out.push(r);
			continue;
		}
		out.push({roomObjID: r.roomObjID, centerline: cleaned, wallThickness: r.wallThickness});
	}
	return out;
}

function computeSharedEdgeKeys(rooms: Array<{id: string, ring: Vec2Simple[]}>): Set<string> {
	const byPair = new Map<string, Set<string>>();
	for (const r of rooms) {
		for (const e of edgesOfRoom(r.ring, r.id, 0)) {
			const k = pairKey(e.start, e.end);
			let s = byPair.get(k);
			if (!s) { s = new Set(); byPair.set(k, s); }
			s.add(r.id);
		}
	}
	const out = new Set<string>();
	for (const [k, ids] of byPair) if (ids.size >= 2) out.add(k);
	return out;
}

// ---------------------------------------------------------------------------
// Merge (§3.3)
// ---------------------------------------------------------------------------
/** The point in `pts` whose `measure` value is extreme (`sign` −1 = minimum, +1 = maximum). Used to
 *  take the union of coincident claimants' mitered corners: they all lie on one offset line, so the
 *  union's extent is just the most-extreme point along the wall. */
function pickExtreme(pts: Vec2Simple[], measure: (p: Vec2Simple)=>number, sign: number): Vec2Simple {
	let best = pts[0];
	let bestVal = measure(best);
	for (const p of pts) {
		const val = measure(p);
		if (sign > 0 ? val > bestVal : val < bestVal) { best = p; bestVal = val; }
	}
	return best;
}

/** The resolved (max-wins, §0 decision 4) thickness for every distinct unordered endpoint pair
 *  claimed by a room edge, over the whole floor. Computed BEFORE the face rings so a shared wall's
 *  thickness — which can exceed either room's own — is what both rooms' miters see (§3.4). Emits the
 *  thickness-conflict warnings. */
function resolveThicknessByPair(rooms: Array<{roomObjID: string, ring: Vec2Simple[], wallThickness: number}>, warnings: string[]): Map<string, number> {
	const byPair = new Map<string, {thickness: number, ids: Set<string>, values: Set<number>}>();
	for (const r of rooms) {
		for (const e of edgesOfRoom(r.ring, r.roomObjID, r.wallThickness)) {
			const k = pairKey(e.start, e.end);
			let g = byPair.get(k);
			if (!g) { g = {thickness: 0, ids: new Set(), values: new Set()}; byPair.set(k, g); }
			g.ids.add(r.roomObjID);
			g.values.add(Math.round(e.wallThickness * 1e6) / 1e6);
			g.thickness = Math.max(g.thickness, e.wallThickness);
		}
	}
	const out = new Map<string, number>();
	for (const [k, g] of byPair) {
		out.set(k, g.thickness);
		if (g.ids.size > 1 && g.values.size > 1) {
			warnings.push(`Shared wall thickness conflict between rooms ${[...g.ids].join(" & ")}: using max ${g.thickness}m.`);
		}
	}
	return out;
}

/** The MITERED face rings of every room (index-aligned with the room's centerline ring), computed
 *  from the floor-wide max-wins thicknesses so a shared wall's miter is consistent on both sides.
 *  Keyed by `roomObjID`. */
function faceRingsPerRoom(rooms: Array<{roomObjID: string, ring: Vec2Simple[], wallThickness: number}>): Map<string, {inner: Vec2Simple[], outer: Vec2Simple[]}> {
	const thicknessByPair = resolveThicknessByPair(rooms, []);
	const out = new Map<string, {inner: Vec2Simple[], outer: Vec2Simple[]}>();
	for (const r of rooms) out.set(r.roomObjID, miteredFaceRings(r.ring, thicknessByPair, r.wallThickness));
	return out;
}

function buildMergedSegments(
	rooms: Array<{roomObjID: string, ring: Vec2Simple[], wallThickness: number}>,
	thicknessByPair: Map<string, number>,
	warnings: string[],
): FloorWallSegment[] {
	type Group = {ids: Set<string>, edges: RawEdge[]};
	const byPair = new Map<string, Group>();
	for (const r of rooms) {
		for (const e of edgesOfRoom(r.ring, r.roomObjID, r.wallThickness)) {
			const k = pairKey(e.start, e.end);
			let g = byPair.get(k);
			if (!g) { g = {ids: new Set(), edges: []}; byPair.set(k, g); }
			g.ids.add(r.roomObjID);
			g.edges.push(e);
		}
	}

	// Mitered faces per room (indexed by the room's ring vertex), so each segment's ends can be
	// replaced by the room's true corner points (§3.4).
	const faceFits = faceRingsPerRoom(rooms);

	const segments: FloorWallSegment[] = [];
	for (const [pairK, group] of byPair) {
		const ids = [...group.ids];
		const shared = ids.length > 1;
		// Resolved thickness: max-wins over claimants (§0 decision 4). Already warned about in
		// `resolveThicknessByPair`; read from the floor-wide map so the two agree exactly.
		const e0 = group.edges[0];
		const thickness = thicknessByPair.get(pairK) ?? Math.max(...group.edges.map(e=>e.wallThickness));
		const start = e0.start, end = e0.end, nrm = e0.normal;
		const dirLen = e0.length || 1e-9;
		const dir: Vec2Simple = [(end[0] - start[0]) / dirLen, (end[1] - start[1]) / dirLen];

		// MITERED ends (§3.4). Each claimant carries its own copy of this edge in its mitered face
		// rings (`miteredFaceRings` is index-aligned with the room's centerline ring, so edge
		// `e.edgeIndex` starts at face vertex `e.edgeIndex` and ends at the next one). The merged
		// segment must span the union of those miters, or a shared wall stops at the first claimant's
		// corner and leaves a notch where the second room's band ends.
		//
		// CRITICAL: a claimant's "inner" face is on ITS OWN side of the centerline, so two rooms
		// sharing a wall put their inner faces on OPPOSITE sides of this segment's normal. The two
		// face families must therefore be separated by their signed cross-wall offset before being
		// unioned — a naive `min/max` over all claimants' inner points would splice the two rooms'
		// faces into one bogus quad. All claimants share one resolved thickness (§0 decision 4), so
		// each family is a single line and "union" reduces to the extreme ALONG the wall.
		const off = (p: Vec2Simple): number=> (p[0] - start[0]) * nrm[0] + (p[1] - start[1]) * nrm[1];
		const half = thickness / 2;
		const innerStarts: Vec2Simple[] = [];
		const innerEnds: Vec2Simple[] = [];
		const outerStarts: Vec2Simple[] = [];
		const outerEnds: Vec2Simple[] = [];
		for (const e of group.edges) {
			const roomRing = rooms.find(r=>r.roomObjID == e.roomObjID)!.ring;
			const face = faceFits.get(e.roomObjID)!;
			const idxEnd = (e.edgeIndex + 1) % roomRing.length;
			innerStarts.push(face.inner[e.edgeIndex]);
			innerEnds.push(face.inner[idxEnd]);
			outerStarts.push(face.outer[e.edgeIndex]);
			outerEnds.push(face.outer[idxEnd]);
		}
		// Per-end face points; if a claimant's ring index did not line up (or a split vertex's
		// miter fell on the wrong side), fall back to the flat ±t/2 point AT THAT END — mixing
		// the fallback into the family keeps both ends independently solvable (sharing one
		// single-point family would collapse the segment's inner face to zero length).
		const flat = (d: number, p: Vec2Simple): Vec2Simple=>[p[0] + nrm[0] * d, p[1] + nrm[1] * d];
		const along = (p: Vec2Simple): number=> (p[0] - start[0]) * dir[0] + (p[1] - start[1]) * dir[1];
		const inFamily = (p: Vec2Simple, side: number)=>Math.abs(off(p) - side) < 1e-6;
		const innerStartsOk = innerStarts.filter(p=>inFamily(p, -half));
		const innerEndsOk = innerEnds.filter(p=>inFamily(p, -half));
		const outerStartsOk = outerStarts.filter(p=>inFamily(p, half));
		const outerEndsOk = outerEnds.filter(p=>inFamily(p, half));
		const innerStart = pickExtreme([...innerStartsOk, flat(-half, start)], along, -1);
		const innerEnd = pickExtreme([...innerEndsOk, flat(-half, end)], along, +1);
		const outerStart = pickExtreme([...outerStartsOk, flat(+half, start)], along, -1);
		const outerEnd = pickExtreme([...outerEndsOk, flat(+half, end)], along, +1);

		segments.push({
			start, end,
			length: e0.length,
			normal: nrm,
			wallThickness: thickness,
			innerStart, innerEnd, outerStart, outerEnd,
			innerLength: Math.hypot(innerEnd[0] - innerStart[0], innerEnd[1] - innerStart[1]),
			claimantRoomIDs: ids,
			shared,
			tJunction: false,
			tJunctionStarts: false,
			tJunctionEnds: false,
		});
	}

	// Join detection (§3.4 / §10 F4): a segment END landing on another segment's INTERIOR — two very different geometries that must NOT be conflated (full rationale in the plan doc's §10 F4 note). A T-JOIN (bands cross) leaves the end cross-section OPEN, so F4 emits a BUTT CAP, recorded PER END because one wall can be a T at both ends. A COLLINEAR SUB-SPAN (bands overlap, e.g. a 2m room pressed against an 8m wall) is NOT a T — the through band backs it on both sides, so a cap there would be a hidden interior face — but the two bands do occupy one strip, so it IS a real defect (a duplicate z-fighting band) and is reported; this is common authoring geometry, so that diagnostic is load-bearing.
	// Each condition reports ONCE PER UNORDERED GEOMETRIC PAIR, with the endpoint in the message: pre-F4 the loop warned per (i, j) hit, so one join produced four byte-identical lines.
	const reportedT = new Set<string>();
	const reportedCollinear = new Set<string>();
	for (let i = 0; i < segments.length; i++) {
		const si = segments[i];
		const siDir = unitDir(si.start, si.end);
		const siKey = pairKey(si.start, si.end);
		const ends: Array<["start"|"end", Vec2Simple]> = [["start", si.start], ["end", si.end]];
		for (const [endName, p] of ends) {
			for (let j = 0; j < segments.length; j++) {
				if (i == j) continue;
				const sj = segments[j];
				const tHit = paramOnSegment(p, sj);
				// `paramOnSegment` returns a UNIT fraction ∈ [0,1]; an endpoint landing exactly at 0/1
				// is a normal corner join (two edges meeting), not a join into an interior.
				if (tHit == null || tHit <= 1e-6 || tHit >= 1 - 1e-6) continue;
				const sjDir = unitDir(sj.start, sj.end);
				// |cross| ~ 0 ⇒ the two walls run along the same line (a collinear sub-span);
				// otherwise the ending wall meets the through wall transversally (a real T).
				const collinear = Math.abs(siDir[0] * sjDir[1] - siDir[1] * sjDir[0]) < 1e-6;
				const at = `(${p[0].toFixed(3)}, ${p[1].toFixed(3)})`;
				const who = `wall of room ${si.claimantRoomIDs.join("/")}`;
				const other = `wall of room ${sj.claimantRoomIDs.join("/")}`;
				if (collinear) {
					const sjKey = pairKey(sj.start, sj.end);
					// Unordered pair of segments → one report per overlapping pair, not per end/edge copy.
					const pair = siKey < sjKey ? `${siKey}|${sjKey}` : `${sjKey}|${siKey}`;
					if (reportedCollinear.has(pair)) continue;
					reportedCollinear.add(pair);
					warnings.push(`Collinear overlapping walls: ${who} runs along ${other} (ends at ${at} inside it) and their bands overlap for ${overlapLength(si, sj).toFixed(2)}m — the merge engine collapses only EXACT whole-edge coincidence, so this partial overlap is drawn as two stacked bands (z-fighting) rather than one wall. Offset one room, or make the shared edge coincide exactly, to author it deliberately.`);
					continue;
				}
				const key = `${i}|${endName}|${j}`;
				if (reportedT.has(key)) continue;
				reportedT.add(key);
				if (endName == "start") si.tJunctionStarts = true;
				else si.tJunctionEnds = true;
				si.tJunction = true;
				warnings.push(`T-junction: ${who} ends at ${at} on the interior of ${other} — the ending wall is butt-capped there.`);
			}
		}
	}
	return segments;
}

/** Unit direction of the segment `a → b` (a zero-length segment yields [0,0], which reads as
 *  "collinear with nothing" via a cross product of 0 — fine, since such a segment is never a join
 *  target). */
function unitDir(a: Vec2Simple, b: Vec2Simple): Vec2Simple {
	const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
	if (len < 1e-12) return [0, 0];
	return [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
}

/** The length over which two COLLINEAR segments' spans overlap (0 when they only touch end-to-end).
 *  Used purely for the diagnostics message, so a scalar projection suffices. */
function overlapLength(a: FloorWallSegment, b: FloorWallSegment): number {
	const dir = unitDir(a.start, a.end);
	const proj = (p: Vec2Simple)=> (p[0] - a.start[0]) * dir[0] + (p[1] - a.start[1]) * dir[1];
	const a0 = 0, a1 = proj(a.end);
	const b0 = proj(b.start), b1 = proj(b.end);
	const lo = Math.max(Math.min(a0, a1), Math.min(b0, b1));
	const hi = Math.min(Math.max(a0, a1), Math.max(b0, b1));
	return Math.max(0, hi - lo);
}

/** Unit fraction along segment `s` where point `p` projects, or null when `p` is off the segment's
 *  axis (perpendicular distance > 1e-3). Returns a NORMALIZED `t ∈ [0,1]` (0 = start, 1 = end). */
function paramOnSegment(p: Vec2Simple, s: FloorWallSegment): number|null {
	const ax = s.start[0], ay = s.start[1];
	const dx = s.end[0] - ax, dy = s.end[1] - ay;
	const l2 = dx * dx + dy * dy;
	if (l2 < 1e-18) return null;
	const t = ((p[0] - ax) * dx + (p[1] - ay) * dy) / l2;
	const perp = distPointSegment(p[0], p[1], ax, ay, s.end[0], s.end[1]);
	// Require on-axis within a hair of tolerance so a wall whose end merely passes near (not
	// through) another is not flagged.
	return perp < 1e-3 ? t : null;
}

// ---------------------------------------------------------------------------
// Differential inner-face ring (per-edge inset: t/2 exterior, 0 shared)
// ---------------------------------------------------------------------------
function innerFaceRing(ring: Vec2Simple[], sharedKeys: Set<string>, wallThickness: number): Vec2Simple[] {
	return perEdgeOffsetRing(ring, sharedKeys, wallThickness, "inward");
}

/** The room's SLAB ring (Set2 §6.2 fix): flush with the centerline on SHARED edges (the two
 *  adjacent rooms' slabs meet there, so together they support the full shared wall band) and
 *  EXPANDED OUTWARD by `t/2` on EXTERIOR edges (the slab reaches the outer face, so the full
 *  exterior wall rests on the slab instead of half of it overhanging). Vertex layout identical
 *  to `innerFaceRing` (index-aligned + miter intersections). */
function slabRing(ring: Vec2Simple[], sharedKeys: Set<string>, wallThickness: number): Vec2Simple[] {
	return perEdgeOffsetRing(ring, sharedKeys, wallThickness, "outward");
}

/** Per-edge offset of the centerline ring: `inset_i = 0` on shared edges, `± wallThickness/2`
 *  on exterior edges (inward for the finish ring, outward for the slab ring). Vertex k sits
 *  between edges (k-1) and k — the intersection of their two offset lines. NOT collapsed: the
 *  ring stays INDEX-ALIGNED with the centerline (vertex k ↔ outline vertex k); the
 *  self-intersection collapse (concave pockets) is deferred to polyline consumers via
 *  `TraceSimpleRing`/`unionCells`. */
function perEdgeOffsetRing(ring: Vec2Simple[], sharedKeys: Set<string>, wallThickness: number, direction: "inward"|"outward"): Vec2Simple[] {
	const n = ring.length;
	if (n < 3) return ring.slice();
	const sign = direction == "inward" ? -1 : 1;
	// Per-edge offset distance (0 for shared arms) + line geometry.
	const insets: number[] = [];
	const dirs: Vec2Simple[] = [];
	const normals: Vec2Simple[] = [];
	const lineAnchors: Vec2Simple[] = []; // ring[i] + sign·inset_i · normal_i (a point on the offset line)
	for (let i = 0; i < n; i++) {
		const a = ring[i], b = ring[(i + 1) % n];
		const dx = b[0] - a[0], dy = b[1] - a[1];
		const len = Math.hypot(dx, dy) || 1e-9;
		const shared = sharedKeys.has(pairKey(a, b));
		const inset = shared ? 0 : wallThickness / 2;
		insets.push(inset);
		dirs.push([dx / len, dy / len] as Vec2Simple);
		const nrm = [dy / len, -dx / len] as Vec2Simple;
		normals.push(nrm);
		lineAnchors.push([a[0] + sign * nrm[0] * inset, a[1] + sign * nrm[1] * inset] as Vec2Simple);
	}
	const verts: Vec2Simple[] = [];
	for (let k = 0; k < n; k++) {
		const ePrev = (k - 1 + n) % n;
		const ek = k;
		const p = lineIntersect(lineAnchors[ePrev], dirs[ePrev], lineAnchors[ek], dirs[ek]);
		if (p == null) {
			// Parallel/collinear incident edges → offset this vertex along the next edge's normal.
			verts.push([ring[k][0] + sign * normals[ek][0] * insets[ek], ring[k][1] + sign * normals[ek][1] * insets[ek]] as Vec2Simple);
		} else {
			verts.push(snapPoint(p, 1e-9));
		}
	}
	return verts;
}

/** One room's wall band as the two index-aligned FACE POLYLINES the 2D overlay draws (Set2 §9) plus
 *  the room's `innerFace` finish ring. Because `innerFaceRing` keeps the centerline's vertex
 *  indexing, `inner[i]` is the interior-face point at the centerline vertex `i` — so the overlay can
 *  draw the faces as continuous lines (outline ∩ inner-set / outline ∩ outer-set) instead of the
 *  F1 fallback's `OffsetOutward` re-derivation (which is NOT miter-exact once a shared wall's
 *  thickness differs from the room's own). */
export type RoomFaceLines = {
	roomObjID: string;
	/** Index-aligned (n vertices, no closing dup) interior-face points. */
	inner: Vec2Simple[];
	/** Index-aligned (n vertices, no closing dup) exterior-face points. */
	outer: Vec2Simple[];
	/** The room's finish ring: the same interior points, consecutively deduped + closed. */
	innerFace: Vec2Simple[];
	/** The room's SLAB ring: flush with the centerline on shared edges, expanded outward by
	 *  `t/2` on exterior ones (§6.2 fix — the slab reaches under the full exterior wall).
	 *  Deduped + closed. */
	slabRing: Vec2Simple[];
	/** The AUTHORED points the centerline was derived from, index-aligned (for the reference line).
	 *  Falls back to the centerline when the caller did not supply `sourceRing`. */
	sourceRing: Vec2Simple[];
};

// ---------------------------------------------------------------------------
// Localized union (cell-cover + coincident cancellation)
// ---------------------------------------------------------------------------
type Feeder = {id: string, ring: Vec2Simple[]};
type UnionEdge = {a: Vec2Simple, b: Vec2Simple, id: string};

/** The union of a set of CCW open rings as a CCW + CLOSED polygon. Excludes coincident shared edges
 *  (>=2 feeders) and edges covered by another feeder; collinear overlaps are merged so the result is
 *  exact for the rectilinear case (the Roof_Pure lattice oracle) and well-formed for the general one. */
function unionCells(feeders0: Feeder[], tol: number): Vec2Simple[] {
	const feeders = feeders0.filter(f=>f.ring.length >= 3);
	if (feeders.length == 0) return [];

	let edges: UnionEdge[] = [];
	for (const f of feeders) {
		const r = f.ring;
		const n = r.length;
		for (let i = 0; i < n; i++) {
			const a = r[i], b = r[(i + 1) % n];
			if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 1e-12) continue;
			edges.push({a, b, id: f.id});
		}
	}
	if (edges.length == 0) return [];

	// Drop coincident SHARED edges (interior walls, not union boundary).
	const byPair = new Map<string, Set<string>>();
	for (const e of edges) {
		const k = pairKey(e.a, e.b);
		let s = byPair.get(k);
		if (!s) { s = new Set(); byPair.set(k, s); }
		s.add(e.id);
	}
	edges = edges.filter(e=> {
		const ids = byPair.get(pairKey(e.a, e.b));
		return !ids || ids.size < 2;
	});

	// Split edges at interior crossings (a vertex-on-edge from another edge → split both at the hit).
	edges = splitAtCrossings(edges, tol);

	// Coverage: keep a (now-split) edge iff its midpoint is NOT strictly interior to another feeder.
	const kept: UnionEdge[] = [];
	for (const e of edges) {
		if (coveredByOther(e, feeders)) continue;
		kept.push(e);
	}
	if (kept.length == 0) return [];

	// Collinear same-orientation merge (dedupe overlapping spans, e.g. two rooms' coincident tops).
	const merged = mergeCollinearSpans(kept, tol);
	const boundary: Array<{start: Vec2Simple, end: Vec2Simple}> = merged.map(e=>({start: e.a, end: e.b}));
	if (boundary.length == 0) return [];

	const traced = TraceEdgesToPolygon(boundary) as Vec2Simple[];
	if (traced.length < 3) return [];
	const optimized = OptimizePolygon(traced);
	if (optimized.length < 3) return [];
	// Re-wind to CCW (TraceEdgesToPolygon may return either) and close.
	let poly = optimized.map(p=>snapPoint(p, tol));
	if (signedArea(poly) < 0) poly = [...poly].reverse();
	return closeRing(poly);
}

function coveredByOther(e: UnionEdge, feeders: Feeder[]): boolean {
	const mx = (e.a[0] + e.b[0]) / 2, my = (e.a[1] + e.b[1]) / 2;
	for (const f of feeders) {
		if (f.id === e.id) continue;
		// Covered == strictly inside the other room AND not hugging its boundary (a boundary-adjacent
		// midpoint like a shared wall is KEPT).
		if (IsPointInPolygon(mx, my, f.ring) && distToRingPoint(mx, my, f.ring) > BOUNDARY_GUARD_EPS) {
			return true;
		}
	}
	return false;
}

/** Split every edge at every other edge's / vertex's interior crossing, so no edge interior crosses
 *  another's interior (making the coverage + collinear-merge + trace well-defined). */
function splitAtCrossings(edgesIn: UnionEdge[], tol: number): UnionEdge[] {
	const out: UnionEdge[] = [];
	for (let i = 0; i < edgesIn.length; i++) {
		const e = edgesIn[i];
		const splits = new Set<number>([0, 1]);
		for (let j = 0; j < edgesIn.length; j++) {
			if (i == j) continue;
			const f = edgesIn[j];
			const hit = segmentCross(e, f, tol);
			if (hit != null) { splits.add(hit[0]); splits.add(hit[1]); }
		}
		for (const p of edgesIn) {
			if (p === e) continue;
			// Any vertex of p lying strictly on e's interior should split e.
			for (const v of [p.a, p.b]) {
				const t = vertexOnSegment(v, e, tol);
				if (t != null && t > 1e-9 && t < 1 - 1e-9) splits.add(t);
			}
		}
		const arr = [...splits].filter(t=>t >= 0 && t <= 1).sort((x, y)=>x - y);
		for (let k = 0; k < arr.length - 1; k++) {
			const t0 = arr[k], t1 = arr[k + 1];
			if (t1 - t0 < 1e-9) continue;
			const a: Vec2Simple = [e.a[0] + (e.b[0] - e.a[0]) * t0, e.a[1] + (e.b[1] - e.a[1]) * t0] as Vec2Simple;
			const b: Vec2Simple = [e.a[0] + (e.b[0] - e.a[0]) * t1, e.a[1] + (e.b[1] - e.a[1]) * t1] as Vec2Simple;
			out.push({a: snapPoint(a, tol), b: snapPoint(b, tol), id: e.id});
		}
	}
	return out;
}

/** If `e` and `f` cross at points interior to BOTH (t0,t1 in (0,1)), return [tOnE, tOnF]; else null. */
function segmentCross(e: UnionEdge, f: UnionEdge, tol: number): [number, number]|null {
	const ax = e.a[0], ay = e.a[1];
	const edx = e.b[0] - ax, edy = e.b[1] - ay;
	const bx = f.a[0], by = f.a[1];
	const fdx = f.b[0] - bx, fdy = f.b[1] - by;
	const denom = edx * fdy - edy * fdx;
	if (Math.abs(denom) < 1e-12) return null; // parallel / collinear → handled by merge, not split
	const rx = bx - ax, ry = by - ay;
	const t = (rx * fdy - ry * fdx) / denom;
	const s = (rx * edy - ry * edx) / denom;
	if (t > tol && t < 1 - tol && s > tol && s < 1 - tol) return [t, s];
	return null; // endpoint-on-interior cases handled by the separate vertex-split pass in splitAtCrossings
}

/** t in [0,1] where vertex `v` lies on segment `e`, or null. */
function vertexOnSegment(v: Vec2Simple, e: UnionEdge, tol: number): number|null {
	const ax = e.a[0], ay = e.a[1];
	const dx = e.b[0] - ax, dy = e.b[1] - ay;
	const l2 = dx * dx + dy * dy;
	if (l2 < 1e-18) return null;
	const t = ((v[0] - ax) * dx + (v[1] - ay) * dy) / l2;
	if (t < -tol / Math.sqrt(l2) || t > 1 + tol / Math.sqrt(l2)) return null;
	if (distPointSegment(v[0], v[1], ax, ay, e.b[0], e.b[1]) > tol * 2) return null;
	return t;
}

/** Merge collinear, same-orientation overlapping spans into maximal spans (dedupe the coincident
 *  top/bottom of two rooms sharing a wall). */
function mergeCollinearSpans(edges: UnionEdge[], tol: number): UnionEdge[] {
	// Group by (integer direction, line offset).
	const groups = new Map<string, UnionEdge[]>();
	for (const e of edges) {
		const gdx = Math.round((e.b[0] - e.a[0]) / tol) || 0;
		const gdy = Math.round((e.b[1] - e.a[1]) / tol) || 0;
		if (gdx == 0 && gdy == 0) continue;
		const g = gcd(gdx, gdy);
		const dkx = gdx / g, dky = gdy / g;
		const s = Math.round((dkx * e.a[1] - dky * e.a[0]) / tol); // cross(dir, point), in tol units
		const key = `${dkx},${dky}|${s}`;
		let grp = groups.get(key);
		if (!grp) { grp = []; groups.set(key, grp); }
		grp.push(e);
	}
	const out: UnionEdge[] = [];
	for (const [key, grp] of groups) {
		const [dkx, dky] = key.split("|")[0].split(",").map(Number);
		const ux = dkx / Math.hypot(dkx, dky), uy = dky / Math.hypot(dkx, dky);
		// Project each edge onto the unit direction → intervals.
		type Int = {lo: number, hi: number, pLo: Vec2Simple, pHi: Vec2Simple};
		const ints: Int[] = grp.map(e=>{
			const ta = e.a[0] * ux + e.a[1] * uy;
			const tb = e.b[0] * ux + e.b[1] * uy;
			return ta <= tb
				? {lo: ta, hi: tb, pLo: e.a, pHi: e.b}
				: {lo: tb, hi: ta, pLo: e.b, pHi: e.a};
		}).sort((x, y)=>x.lo - y.lo);
		// Merge overlapping/adjacent intervals into maximal spans.
		let cur: Int|null = null;
		for (const int of ints) {
			if (cur == null) { cur = int; continue; }
			if (int.lo - cur.hi <= tol) {
				cur.hi = Math.max(cur.hi, int.hi);
				if (int.hi >= cur.hi) cur.pHi = int.pHi;
			} else {
				out.push({a: cur.pLo, b: cur.pHi, id: "-"});
				cur = int;
			}
		}
		if (cur != null) out.push({a: cur.pLo, b: cur.pHi, id: "-"});
	}
	return out;
}

// ---------------------------------------------------------------------------
// Thin-cavity detection (§3.3.3)
// ---------------------------------------------------------------------------
function detectThinCavities(rooms: Array<{roomObjID: string, centerline: Vec2Simple[]}>, tol: number, warnings: string[]): void {
	const allEdges: Array<{start: Vec2Simple, end: Vec2Simple, roomObjID: string}> = [];
	for (const r of rooms) for (const e of edgesOfRoom(r.centerline, r.roomObjID, 0)) allEdges.push({start: e.start, end: e.end, roomObjID: r.roomObjID});
	for (let i = 0; i < allEdges.length; i++) {
		for (let j = i + 1; j < allEdges.length; j++) {
			const A = allEdges[i], B = allEdges[j];
			// A room's OWN edges are never a "cavity" between two rooms. Without this guard a convex
			// corner or a staircase notch inside one room's ring fires a bogus "thin cavity between
			// A/A" warning (the separation of two of its own parallel walls is legitimately small).
			if (A.roomObjID == B.roomObjID) continue;
			const adx = A.end[0] - A.start[0], ady = A.end[1] - A.start[1];
			const bdx = B.end[0] - B.start[0], bdy = B.end[1] - B.start[1];
			const lA = Math.hypot(adx, ady), lB = Math.hypot(bdx, bdy);
			if (lA < 1e-12 || lB < 1e-12) continue;
			const dot = Math.abs((adx * bdx + ady * bdy) / (lA * lB));
			if (dot < 1 - 1e-4) continue; // not parallel
			// Perpendicular separation between the two infinite lines.
			const nx = -ady / lA, ny = adx / lA;
			const sep = Math.abs((B.start[0] - A.start[0]) * nx + (B.start[1] - A.start[1]) * ny);
			if (sep > tol && sep < THIN_CAVITY_TOL) {
				// Projections overlap along A's axis?
				const ox = adx / lA, oy = ady / lA;
				const projA0 = 0, projA1 = (A.end[0] - A.start[0]) * ox + (A.end[1] - A.start[1]) * oy;
				const pB0 = (B.start[0] - A.start[0]) * ox + (B.start[1] - A.start[1]) * oy;
				const pB1 = (B.end[0] - A.start[0]) * ox + (B.end[1] - A.start[1]) * oy;
				const lo = Math.min(projA0, projA1), hi = Math.max(projA0, projA1);
				const blo = Math.min(pB0, pB1), bhi = Math.max(pB0, pB1);
				if (blo < hi - tol && bhi > lo + tol) {
					// Skip if this pair is an actual shared edge (same unordered pair).
					if (pairKey(A.start, A.end) === pairKey(B.start, B.end)) continue;
					warnings.push(`Thin cavity: rooms ${A.roomObjID}/${B.roomObjID} have near-coincident parallel walls ~${sep.toFixed(3)}m apart (< 5cm) — likely an authoring slip, not merged.`);
				}
			}
		}
	}
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------
export function MergeFloorWalls_Pure(input: FloorWallMergeInput): FloorWallPlan {
	const tol = input.coincidenceTolerance ?? DEFAULT_COINCIDENCE_TOL;
	const warnings: string[] = [];

	// Normalize each room: drop closing dup, snap to tol grid, re-wind CCW, drop degenerates.
	const rooms: Array<{roomObjID: string, centerline: Vec2Simple[], wallThickness: number}> = [];
	for (const r of input.rooms) {
		const eq = Math.max(r.wallThickness, 0);
		const ring = NormalizeFootprint(r.centerline.map(p=>snapPoint(p, tol)), false);
		if (ring.length < 3 || Math.abs(signedArea(ring)) < 1e-12) {
			warnings.push(`Room ${r.roomObjID}: degenerate/zero-area centerline skipped.`);
			continue;
		}
		if (eq <= 0) warnings.push(`Room ${r.roomObjID}: wallThickness <= 0; clamped to a minimum.`);
		rooms.push({roomObjID: r.roomObjID, centerline: ring, wallThickness: Math.max(eq, 1e-6)});
	}
if (rooms.length == 0) return {segments: [], centerlineUnion: [], innerFaceUnion: [], innerFacePerRoom: [], slabRingPerRoom: [], wallsPerRoom: [], warnings};

	// §11 collinear sub-span fix: when one room's edge overlaps PART of another room's edge
	// (e.g. a 4m room edge along the middle of an 11m wall), the exact-key grouping leaves two
	// stacked bands (z-fighting) and — critically — an Opening punches only ONE band while the
	// other stays solid, blocking a passage the author meant to open. Split BOTH edges at every
	// interior endpoint of the other's span, so the overlapping sub-spans become exact-key
	// coincident and merge into ONE wall. Done on the RINGS (before every downstream pass:
	// sharedKeys, thickness, faces, merge), so each derived structure stays index-aligned with
	// the ring it was derived from.
	const splitRooms = splitCollinearOverlaps(rooms, tol, warnings);

	const sharedKeys = computeSharedEdgeKeys(splitRooms.map(r=>({id: r.roomObjID, ring: r.centerline})));

	// Resolve every shared wall's thickness FIRST (max-wins, §0 decision 4) so both the segments and
	// the per-room mitered faces agree on it (§3.4). Warnings are emitted here, once.
	const thicknessByPair = resolveThicknessByPair(splitRooms.map(r=>({roomObjID: r.roomObjID, ring: r.centerline, wallThickness: r.wallThickness})), warnings);

	const segments = buildMergedSegments(splitRooms.map(r=>({roomObjID: r.roomObjID, ring: r.centerline, wallThickness: r.wallThickness})), thicknessByPair, warnings);

	const centerlineUnion = unionCells(splitRooms.map(r=>({id: r.roomObjID, ring: r.centerline})), tol);
	detectThinCavities(splitRooms, tol, warnings);

	// Per-room wall band: the aligned face polylines (2D overlay) + the finish ring (slab/ceiling).
	const sourceByRoom = new Map<string, Vec2Simple[]>();
	for (const r of input.rooms) if (r.sourceRing != null) sourceByRoom.set(r.roomObjID, r.sourceRing);
	const wallsPerRoom: RoomFaceLines[] = splitRooms.map(r=>{
		const faces = miteredFaceRings(r.centerline, thicknessByPair, r.wallThickness);
		const innerFace = closeRing(dedupConsecutive(innerFaceRing(r.centerline, sharedKeys, r.wallThickness)));
		const slab = closeRing(dedupConsecutive(slabRing(r.centerline, sharedKeys, r.wallThickness)));
		return {
			roomObjID: r.roomObjID,
			inner: faces.inner,
			outer: faces.outer,
			innerFace,
			slabRing: slab,
			sourceRing: sourceByRoom.get(r.roomObjID) ?? r.centerline,
		};
	});
	const innerFacePerRoom = wallsPerRoom.map(w=>({roomObjID: w.roomObjID, ring: w.innerFace}));
	const slabRingPerRoom = wallsPerRoom.map(w=>({roomObjID: w.roomObjID, ring: w.slabRing}));
	// The union needs SIMPLE rings, so collapse self-intersections (concave pockets) here rather
	// than in `innerFaceRing` (which must stay index-aligned with the centerline).
	const innerFaceUnion = unionCells(innerFacePerRoom.map(p=>({id: p.roomObjID, ring: TraceSimpleRing(p.ring)})), tol);

	return {segments, centerlineUnion, innerFaceUnion, innerFacePerRoom, slabRingPerRoom, wallsPerRoom, warnings};
}

/** Collapse a ring's self-intersections (concave pockets) so it can be fed to the union's edge
 *  tracer. Kept here rather than in `innerFaceRing` because the per-room output must stay
 *  index-aligned with the centerline ring (see `innerFaceRing`'s note). */
function TraceSimpleRing(ring: Vec2Simple[]): Vec2Simple[] {
	return CollapseSelfIntersections(ring);
}

/** Drop consecutive duplicate points (keeping the first) — used to turn an index-aligned face
 *  polyline into a clean drawable/closed ring. */
function dedupConsecutive(pts: Vec2Simple[]): Vec2Simple[] {
	const out: Vec2Simple[] = [];
	for (const p of pts) {
		const last = out.length ? out[out.length - 1] : null;
		if (last == null || Math.abs(p[0] - last[0]) > 1e-9 || Math.abs(p[1] - last[1]) > 1e-9) out.push(p);
	}
	return out;
}
